import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Megaphone, RefreshCw, Settings, Search, Phone, Mail } from "lucide-react";
import { MetaSettingsDialog } from "@/components/MetaSettingsDialog";

const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["converted", "Converted"], ["lost", "Lost"]];
const SCLS = {
  new: "bg-sky-500/15 text-sky-700 dark:text-sky-400", contacted: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  converted: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", lost: "bg-neutral-500/15 text-muted-foreground",
};
const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const StatusSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} onClick={(e) => e.stopPropagation()} className={`h-8 px-2 text-[11px] font-bold uppercase border border-border ${SCLS[value] || ""}`} data-testid={testid}>
    {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

const LeadSheet = ({ lead, onClose, onSaved }) => {
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (!lead) return;
    api.get(`/meta/leads/${lead.id}`).then((r) => { setD(r.data); setNotes(r.data.notes || ""); onSaved(r.data, true); }).catch((e) => toast.error(apiErr(e)));
  }, [lead]); // eslint-disable-line
  const save = async (patch) => {
    try { const { data } = await api.patch(`/meta/leads/${lead.id}`, patch); setD((x) => ({ ...x, ...data })); onSaved(data); toast.success("Lead updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="meta-lead-sheet">
        <SheetHeader><SheetTitle className="font-heading uppercase">{lead?.name || "Lead"}</SheetTitle><SheetDescription>{lead?.form_name} · {fmt(lead?.created_time)}</SheetDescription></SheetHeader>
        {d && (
          <div className="mt-4 space-y-4 text-sm">
            <div className="space-y-1">
              {d.phone && <a href={`tel:${d.phone}`} className="flex items-center gap-2 hover:underline"><Phone className="h-4 w-4" /> {d.phone}</a>}
              {d.email && <a href={`mailto:${d.email}`} className="flex items-center gap-2 hover:underline"><Mail className="h-4 w-4" /> {d.email}</a>}
              <div className="text-xs text-muted-foreground">Campaign: {d.campaign_name || "—"} · Ad: {d.ad_name || "—"}</div>
            </div>
            <div><div className="text-xs font-bold uppercase mb-1">Status</div><StatusSelect value={d.status} onChange={(v) => save({ status: v })} testid="meta-sheet-status" /></div>
            <div className="border border-border divide-y divide-border">
              {(d.field_data || []).map((x) => <div key={x.name} className="p-2 grid grid-cols-[120px_1fr] gap-2 text-xs"><span className="text-muted-foreground">{x.name}</span><span>{(x.values || []).join(", ")}</span></div>)}
            </div>
            <div>
              <div className="text-xs font-bold uppercase mb-1">Internal notes</div>
              <Textarea rows={5} className="rounded-none" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="meta-sheet-notes" />
              <Button className="rounded-none mt-2" onClick={() => save({ notes })} data-testid="meta-sheet-save-notes">Save notes</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const LeadRow = ({ r, onOpen, onStatus }) => (
  <tr onClick={() => onOpen(r)} className={`border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${r.is_new ? "bg-primary/5" : ""}`} data-testid={`meta-row-${r.id}`}>
    <td className="px-4 py-3 font-semibold">
      {r.name || "—"}
      {r.is_new && <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-primary text-primary-foreground" data-testid={`meta-new-badge-${r.id}`}>New</span>}
    </td>
    <td className="px-4 font-mono-num">{r.phone || "—"}</td>
    <td className="px-4 truncate max-w-[200px]">{r.email || "—"}</td>
    <td className="px-4 text-xs">{r.campaign_name || "—"}</td>
    <td className="px-4 text-xs">{r.form_name || "—"}</td>
    <td className="px-4 text-xs whitespace-nowrap">{fmt(r.created_time)}</td>
    <td className="px-4"><StatusSelect value={r.status} onChange={(v) => onStatus(r, v)} testid={`meta-status-${r.id}`} /></td>
  </tr>
);

export default function MetaLeadsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ leads: [], counts: {}, settings: {} });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get(`/meta/leads?status=${status}&search=${encodeURIComponent(q)}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [status, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { const { data: r } = await api.post("/meta/sync"); toast.success(r.skipped ? "A sync is already running" : `Synced · ${r.new} new lead(s)`); load(); }
    catch (e) { toast.error(apiErr(e)); load(); } finally { setSyncing(false); }
  };

  const patchRow = (row, opened) => setData((d) => ({ ...d, leads: d.leads.map((x) => (x.id === row.id ? { ...x, ...row, field_data: undefined, is_new: opened ? false : row.is_new } : x)) }));
  const setRowStatus = async (r, v) => {
    try { const { data: row } = await api.patch(`/meta/leads/${r.id}`, { status: v }); patchRow(row); } catch (e) { toast.error(apiErr(e)); }
  };

  const s = data.settings || {};
  const c = data.counts || {};
  return (
    <div className="space-y-4" data-testid="meta-leads-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <div className="text-xs uppercase tracking-widest text-primary font-bold mb-1">Marketing</div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight uppercase flex items-center gap-2"><Megaphone className="h-6 w-6 text-primary" /> Meta Leads</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="meta-last-synced">Last synced: <b className="text-foreground">{fmt(s.last_synced_at)}</b> · auto-sync every 15 min{s.page_name ? ` · ${s.page_name}` : ""}</p>
          {s.last_error && <p className="text-xs text-destructive mt-1" data-testid="meta-last-error">Last sync problem: {s.last_error}</p>}
        </div>
        {user?.role === "admin" && <Button variant="outline" className="rounded-none gap-1.5" onClick={() => setShowSettings(true)} data-testid="meta-settings-btn"><Settings className="h-4 w-4" /> Settings</Button>}
        <Button className="rounded-none gap-1.5 font-bold uppercase" onClick={sync} disabled={syncing} data-testid="meta-sync-btn"><RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Manual Sync"}</Button>
      </div>

      {!s.has_token && <div className="border border-amber-500/40 bg-amber-500/10 text-sm p-3 text-amber-800 dark:text-amber-300" data-testid="meta-setup-hint">Connect your Facebook Page: open <b>Settings</b>, enter your Meta App ID, App Secret and Page Access Token, then pick the Page and Lead Forms.</div>}

      <div className="flex flex-wrap gap-2 items-center">
        {[["", "All"], ...STATUSES].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} className={`px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${status === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`meta-filter-${k || "all"}`}>
            {l}{k && <span className="ml-1.5 opacity-70">{c[k] || 0}</span>}
          </button>
        ))}
        {c.unseen > 0 && <span className="text-xs font-bold text-primary ml-1" data-testid="meta-unseen-count">{c.unseen} unopened</span>}
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, campaign…" className="rounded-none pl-9 h-9 bg-card" data-testid="meta-search" />
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>{["Lead Name", "Phone Number", "Email", "Ad Campaign", "Lead Form", "Date Submitted", "Status"].map((h) => <th key={h} className="px-4 py-2.5 text-left">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.leads.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground" data-testid="meta-empty">No Meta leads yet.</td></tr>}
            {data.leads.map((r) => <LeadRow key={r.id} r={r} onOpen={setOpen} onStatus={setRowStatus} />)}
          </tbody>
        </table>
      </div>

      <LeadSheet lead={open} onClose={() => setOpen(null)} onSaved={patchRow} />
      <MetaSettingsDialog open={showSettings} onClose={() => setShowSettings(false)} onSaved={load} />
    </div>
  );
}
